import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { createSdkManager, getSdkManager } from '@/sdkManagerSetup';

const AUTH_STORAGE_KEY = 'aol_credentials';

const AuthContext = createContext({
  isAuthenticated: false,
  isLoading: true,
  error: null,
  jwtToken: null,
  login: () => Promise.resolve(),
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

// Simple credential storage helpers
const saveCredentials = creds => {
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, btoa(JSON.stringify(creds)));
  } catch (e) {
    console.warn('Failed to save credentials', e);
  }
};

const getCredentials = () => {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(atob(stored));
  } catch (e) {
    console.warn('Failed to parse credentials, clearing storage');
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
};

const clearCredentials = () => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
};

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get JWT token from SDK manager or credentials
  const getJwtToken = useCallback(() => {
    const sdkManager = getSdkManager();
    return sdkManager?.loginService?.accessToken || window.wbSdk?.loginService?.accessToken || null;
  }, []);

  // Check for existing credentials on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const credentials = getCredentials();
        if (!getSdkManager()) {
          const re = await createSdkManager({ tenantId: credentials?.tenantId }); //try login with empty username password
          setIsAuthenticated(re);
        }
      } catch (err) {
        console.warn('Auto-login failed:', err);
      } finally {
        setIsLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = useCallback(async credentials => {
    setIsLoading(true);
    setError(null);
    try {
      const re = await createSdkManager(credentials);
      saveCredentials(credentials);
      setIsAuthenticated(re);
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.message || 'Login failed.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Simple logout: clear credentials and reload page
  // This avoids SDK teardown issues during React unmount
  const logout = useCallback(() => {
    const sdkManager = getSdkManager();
    sdkManager.loginService.logout(true, 3);
    clearCredentials();
    window.location.reload();
  }, []);

  const jwtToken = getJwtToken();

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, error, jwtToken, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
